import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { HardHat, Mail, MessageSquare, Phone } from "lucide-react";
import ContactFormModal from "@/components/landing/ContactFormModal";

/**
 * UnderConstructionLock
 *
 * Fullscreen, non-dismissible overlay that sits above every landing
 * section while the studio is between physical locations. Blurs the
 * cinematic bokeh underneath (heavy backdrop-blur + colored orbs) so
 * the site still reads as SB Media's aesthetic — but no visitor can
 * interact with pricing, booking, or spotlight flows until we're back.
 *
 * Only the landing page (`/`) gets locked. Admin routes, /portal,
 * /legal, /links, /invoice, and every other page render normally
 * because this component is only mounted inside <Landing /> in App.js.
 *
 * The Contact us CTA reuses the existing Discovery Call flow via
 * `sb:open-discovery`. The Call us CTA is a normal `tel:` link that
 * defers to `REACT_APP_STUDIO_PHONE` if set, falling back to the
 * schema.org placeholder in `/public/index.html`.
 */
const STUDIO_PHONE =
  process.env.REACT_APP_STUDIO_PHONE || "+1-863-000-0000";
const STUDIO_EMAIL =
  process.env.REACT_APP_STUDIO_EMAIL || "info@sbmediastudio.com";

const UnderConstructionLock = () => {
  // Lock body scroll on every browser (iOS Safari's rubber-band needs the
  // `position: fixed` trick, not just `overflow: hidden`). Restores the
  // exact scroll position on unmount so removing the lockout later
  // doesn't teleport the user.
  useEffect(() => {
    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Mobile devices choke on 4 big animated blurred orbs at 60fps — drop
  // the animation entirely on small viewports and keep them as static
  // radial washes. Also disables the motion for anyone with reduced-motion
  // preferences.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(
      "(max-width: 768px), (prefers-reduced-motion: reduce)",
    );
    const onChange = () => setReduceMotion(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const [contactOpen, setContactOpen] = useState(false);

  const openContact = () => {
    setContactOpen(true);
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-5 md:p-8 overflow-hidden"
      style={{ touchAction: "none", overscrollBehavior: "contain" }}
      data-testid="under-construction-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="under-construction-title"
    >
      {/* Heavy blur over everything behind + a nearly-opaque wash. The
          base opacity is 95% so mobile browsers that can't render
          backdrop-blur (older Safari, low-end Android) still fully hide
          the landing underneath — the blur becomes a cinematic bonus
          on capable devices rather than the only thing hiding the site. */}
      <div className="absolute inset-0 bg-[#05050a]/95 backdrop-blur-2xl" />

      {/* Bokeh orbs — animated on desktop only. On mobile / reduced-motion
          they render as static radial washes so scroll doesn't fight the
          compositor. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {reduceMotion ? (
          <>
            <div className="absolute top-[8%] left-[10%] w-[380px] h-[380px] rounded-full bg-[#00f0ff]/25 blur-[110px]" />
            <div className="absolute bottom-[6%] right-[8%] w-[440px] h-[440px] rounded-full bg-[#ff0055]/20 blur-[120px]" />
            <div className="absolute top-[38%] right-[22%] w-[320px] h-[320px] rounded-full bg-[#a78bfa]/20 blur-[100px]" />
          </>
        ) : (
          <>
            <motion.div
              initial={{ scale: 0.9, opacity: 0.55 }}
              animate={{
                scale: [0.9, 1.05, 0.95, 1],
                opacity: [0.55, 0.75, 0.5, 0.7],
              }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              style={{ willChange: "transform, opacity" }}
              className="absolute top-[8%] left-[10%] w-[540px] h-[540px] rounded-full bg-[#00f0ff]/30 blur-[140px]"
            />
            <motion.div
              initial={{ scale: 1, opacity: 0.4 }}
              animate={{
                scale: [1, 0.9, 1.1, 1],
                opacity: [0.4, 0.65, 0.35, 0.55],
              }}
              transition={{
                duration: 16,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
              style={{ willChange: "transform, opacity" }}
              className="absolute bottom-[6%] right-[8%] w-[620px] h-[620px] rounded-full bg-[#ff0055]/25 blur-[160px]"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0.35 }}
              animate={{
                scale: [0.95, 1.1, 0.98, 1.05],
                opacity: [0.35, 0.55, 0.3, 0.5],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4,
              }}
              style={{ willChange: "transform, opacity" }}
              className="absolute top-[35%] right-[25%] w-[460px] h-[460px] rounded-full bg-[#a78bfa]/25 blur-[130px]"
            />
            <motion.div
              initial={{ scale: 1.05, opacity: 0.25 }}
              animate={{
                scale: [1.05, 0.95, 1.05],
                opacity: [0.25, 0.45, 0.28],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6,
              }}
              style={{ willChange: "transform, opacity" }}
              className="absolute top-[55%] left-[30%] w-[380px] h-[380px] rounded-full bg-[#f59e0b]/20 blur-[120px]"
            />
          </>
        )}
      </div>

      {/* Scanline overlay so the bokeh isn't flat */}
      <div className="absolute inset-0 scanline opacity-[0.15] pointer-events-none" />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-lg rounded-3xl border border-white/15 bg-gradient-to-b from-[#0e0718]/95 to-[#05050a]/95 backdrop-blur-md shadow-2xl shadow-black/70 overflow-hidden"
        data-testid="under-construction-panel"
      >
        {/* Inner accent ring */}
        <div className="absolute inset-0 rounded-3xl pointer-events-none">
          <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-[#00f0ff]/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 w-64 h-64 rounded-full bg-[#ff0055]/15 blur-3xl" />
        </div>

        <div className="relative p-8 md:p-10 text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#f5c065]/40 bg-[#f5c065]/10 text-[#f5c065] mb-6">
            <HardHat size={12} />
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold">
              Site under construction
            </span>
          </div>

          {/* Hanging "Out for Lunch" sign — pyramid V-cord from a single
              anchor point, itself dangling from a stubby C-stand arm.
              The sign sways gently; the C-stand stays put. */}
          <div className="mb-6 flex flex-col items-center relative pt-14">
            {/* C-stand: partial vertical pole + horizontal arm with
                knuckle + end cap + tiny yellow hook where the cord ties.
                Positioned so the arm-tip lands directly above the sign
                center. */}
            <svg
              aria-hidden="true"
              width="120"
              height="52"
              viewBox="0 0 120 52"
              className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none"
              data-testid="c-stand"
            >
              {/* Vertical pole, coming from above the panel */}
              <line
                x1="18"
                y1="-4"
                x2="18"
                y2="24"
                stroke="#d4d4d8"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* Knuckle / joint */}
              <circle
                cx="18"
                cy="24"
                r="4"
                fill="#0e0718"
                stroke="#a395c3"
                strokeWidth="1.5"
              />
              <circle cx="18" cy="24" r="1.2" fill="#a395c3" />
              {/* Horizontal arm reaching to sign center */}
              <line
                x1="18"
                y1="24"
                x2="60"
                y2="24"
                stroke="#d4d4d8"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* End cap at arm tip */}
              <circle cx="60" cy="24" r="3.2" fill="#e4e4e7" />
              {/* Small hook + cord knot where the pyramid string ties */}
              <line
                x1="60"
                y1="24"
                x2="60"
                y2="34"
                stroke="#f5c065"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="60" cy="35" r="1.6" fill="#f5c065" />
            </svg>

            {/* Sign + V-cord wrapper — swings from the C-stand hook */}
            <motion.div
              initial={{ rotate: -2 }}
              animate={{ rotate: [-2.4, 2.4, -1.7, 1.7, -2.4] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              style={{ transformOrigin: "50% -18px" }}
              className="relative"
              data-testid="out-for-lunch-sign"
            >
              {/* Single V-cord (pyramid) from apex above sign down to
                  both top corners. Rendered as one SVG path so it
                  stays a clean triangle at any width. */}
              <svg
                aria-hidden="true"
                className="absolute -top-5 left-0 w-full pointer-events-none"
                height="22"
                viewBox="0 0 100 22"
                preserveAspectRatio="none"
              >
                <path
                  d="M 50 0 L 6 22 M 50 0 L 94 22"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="1"
                  fill="none"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>

              {/* The sign itself — dark plate with cinematic glow */}
              <div className="relative rounded-xl border border-white/20 bg-gradient-to-br from-[#0a0510] to-[#11091a] px-5 py-3 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.65)]">
                {/* Inner glow ring */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-xl border border-[#00f0ff]/25 pointer-events-none"
                />
                <span
                  aria-hidden
                  className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-[#00f0ff]/25 via-transparent to-[#ff0055]/25 blur-md opacity-70 -z-10"
                />

                <div className="text-[8px] uppercase tracking-[0.34em] text-[#a395c3] text-center leading-none mb-1">
                  SB Media
                </div>
                <div className="font-display font-black text-lg md:text-xl tracking-tight leading-none whitespace-nowrap">
                  <span className="italic text-white">Out for </span>
                  <span className="italic text-[#00f0ff] text-glow-cyan">lunch</span>
                </div>
              </div>
            </motion.div>
          </div>

          <h1
            id="under-construction-title"
            className="font-display font-black text-3xl md:text-[38px] tracking-tighter leading-[1.05] mb-3"
            data-testid="under-construction-title"
          >
            <span className="block">Coming</span>
            <span
              className="inline-block italic font-light text-white/70 text-2xl md:text-[30px] tracking-tight -my-1"
              style={{ transform: "rotate(-8deg)" }}
            >
              Back
            </span>
            <span className="block text-[#00f0ff]">Soon.</span>
          </h1>

          <p className="text-[#a395c3] text-sm md:text-base leading-relaxed max-w-md mx-auto mb-8">
            Something exciting is on the way. In the meantime, we&apos;re
            still creating — please reach out if you need an event captured.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={openContact}
              data-testid="under-construction-contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm shadow-lg shadow-[#00f0ff]/25 hover:bg-white hover:shadow-[#00f0ff]/45 active:scale-[0.98] transition-all"
            >
              <MessageSquare size={14} strokeWidth={2.5} />
              Contact us
            </button>
            <a
              href={`tel:${STUDIO_PHONE.replace(/[^0-9+]/g, "")}`}
              data-testid="under-construction-call"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/[0.06] border border-white/15 text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] hover:border-white active:scale-[0.98] transition-all"
            >
              <Phone size={14} strokeWidth={2.5} />
              Call us
            </a>
            <a
              href={`mailto:${STUDIO_EMAIL}?subject=${encodeURIComponent("Booking inquiry — SB Media")}`}
              data-testid="under-construction-email"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/[0.06] border border-white/15 text-white font-semibold text-sm hover:bg-[#ff0055] hover:border-[#ff0055] hover:text-white hover:shadow-lg hover:shadow-[#ff0055]/30 active:scale-[0.98] transition-all"
            >
              <Mail size={14} strokeWidth={2.5} />
              Email us
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-white/[0.08] text-[10px] uppercase tracking-[0.28em] text-white/40">
            Lakeland, FL · New location coming soon
          </div>
        </div>
      </motion.div>

      {/* Contact form modal — the "Contact us" CTA opens this, not the
          booking calendar. Rendered inside the lockout so it inherits
          the same stacking context and paints cleanly above z-300. */}
      <ContactFormModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
};

export default UnderConstructionLock;
